































































import SVGInteractionHandler from "@/libs/graphics/SVGInteractionHandler";
import ZoomControl from "@/components/Globals/molecule.ugc.zoomControl.vue";
import {
  GDMState,
  GraphicComponent,
  GraphicComponentTypes,
  IGraphicGDM,
  GraphicPlacement,
  GraphicProduct,
  IGraphic,
  IGraphicsControllerStoreState,
  IGraphicSetting,
  PlacementSetting,
  PlacementTypes,
  IViewBox,
} from "@/libs/graphics/types/Graphics.t";
import Vue from "vue";
import GraphicSvgEditor from "@/components/GraphicSvgEditor";
import SvgService from "@vfdp-customs/customs-svg-services";
import BaseSpinner from "@/components/Spinner";
import CanvasRenderer from "@/libs/graphics/CanvasRenderer";
import { IPDM, SVGSize } from "@/store/modules/utils/util.t";
import { IComponentRecipe } from "@/store/modules/recipe/recipe.t";
import { getUGCDataByID, setUGCData, UGCDataStore } from "@/libs/graphics/UGCData";
import { checkImageOutOfBounds } from "@/libs/graphics/UGCHandler";
import { fetchAndInsertImageToGraphicSettings } from "@/libs/graphics/GraphicImageAssetProcesser";
import { ISwatch } from "@/store/modules/swatches/swatches.t";
import FormatRecipeForSVGService from "@/libs/graphics/FormatRecipeForSVGService";
import CreateWrapperSVG from "@/libs/graphics/CreateWrapperSVG";
import { sessionKeys } from '@/data/constants';

const TWEEN = require("@tweenjs/tween.js");
const cloneDeep = require("lodash/cloneDeep");

interface NumericCssAnimatedAttribute {
  [key: string]: number;
}

interface BackgroundStyle {
  "background-color"?: string;
  "background-image"?: string;
}

export default Vue.extend({
  name: "graphicsController",
  components: {
    GraphicSvgEditor,
    BaseSpinner,
    ZoomControl
  },
  data() {
    return {
      showEditor: false as boolean,
      customsSvgService: SvgService,
      svgModels: {} as Record<string, any>,
      svgInteractionHandler: null as null | SVGInteractionHandler,
      svgContentStr: "",
      isLoading: false,
      builtGraphicID: undefined as number | undefined,
      lastGraphicComponent: "",
      precreatedRecipe: false,
      firstLoad: true,
      zoomValue: "100" as string,
      maxValue: 199 as number,
      minValue: 1 as number,
    };
  },
  computed: {
    configuration(): any {
      return this.$store.state.utils.configuration;
    },
    queryParams(): any {
      return this.$store.state.utils.queryParams;
    },
    graphicsController(): IGraphicsControllerStoreState {
      return this.$store.state.graphicsController;
    },
    showPreloader(): boolean {
      return this.$store.state.preloader.showPreloader;
    },
    gdm(): GDMState {
      return this.$store.state.gdm;
    },
    svgSize(): SVGSize {
      return (this.$store.state.utils.pdm as IPDM).components[
        this.activeComponent
      ].svg_size!;
    },
    recipe(): any {
      return this.$store.state.recipe;
    },
    assetsPath(): string {
      return this.$store.state.endPoints.customizer.customsAssets;
    },
    availableSwatches(): ISwatch[] {
      return this.$store.state.utils.availableSwatches;
    },
    swatchBackgroundStyle(): BackgroundStyle {
      const shirt_body = this.recipe.recipe_v2.components?.shirt_body;
      const swatch_id = shirt_body?.swatch_id || -1;
      const swatch: ISwatch = this.availableSwatches.find(
        (s: ISwatch) => Number(s.id) === swatch_id
      ) as ISwatch;
      let style: BackgroundStyle = {};
      if (swatch?.image_assets?.ui)
        style["background-image"] = `url(${this.$store.getters[
          "utils/addCacheBusting"
        ](
          this.$store.getters["endPoints/swatchesPath"]() +
            swatch.image_assets.ui
        )})`;
      else if (swatch?.color) style["background-color"] = swatch.color;
      return style;
    },
    editorVisible(): boolean {
      return this.graphicsController.editorVisible;
    },
    activeComponent(): PlacementTypes {
      // checks if the component is the shirt_body in order to show the graphic of the previous edited component
      const component =
        this.$store.state.standardNavigation.activeComponent !== "shirt_body" ||
        !this.lastGraphicComponent
          ? this.$store.state.standardNavigation.activeComponent
          : this.lastGraphicComponent;
      return component !== "graphic_sleeve_left"
        ? component
        : "graphic_sleeve_right";
    },
    activeSubComponent(): GraphicComponentTypes {
      return this.$store.state.standardNavigation.activeSubComponent;
    },
    componentsWithGraphicsSupport(): PlacementTypes[] {
      return this.$store.getters["utils/componentsWithGraphicsSupport"];
    },
    activeGraphicID(): number | undefined {
      return this.graphicByComponent[this.activeComponent];
    },
    showQuickAdjust(): boolean {
      return this.activeComponent &&
        this.recipe?.recipe_v2?.components &&
        !this.recipe?.recipe_v2?.components[
          this.$store.state.standardNavigation.activeComponent
        ]
        ? false
        : Boolean(this.activeGraphicID);
    },
    activeComponentAndGraphic(): string {
      const val = `${this.activeComponent}/${
        this.graphicByComponent[this.activeComponent]
      }`;
      return val;
    },
    placementSetting(): PlacementSetting[] {
      return this.graphicsController.placementSetting as PlacementSetting[];
    },
    activePlacementSetting(): PlacementSetting {
      return this.graphicsController.placementSetting.find(
        (item: PlacementSetting) =>
          item.placementComponent === this.activeComponent
      ) as PlacementSetting;
    },
    graphicSettings(): Record<string, IGraphicSetting | null> {
      const map: Record<string, IGraphicSetting | null> = {};
      for (let component of this.componentsWithGraphicsSupport) {
        const activeComponent = this.precreatedRecipe
          ? component
          : this.activeComponent;
        const graphic: IGraphicGDM = this.gdm.data.find((item: IGraphic) => {
          return item.id === this.graphicByComponent[component];
        }) as IGraphicGDM;
        const product: GraphicProduct = graphic?.product.find(
          (item: GraphicProduct) =>
            item.productID === this.$store.state.utils.pdmModel
        ) as GraphicProduct;
        const placement: GraphicPlacement = product?.placement.find(
          (item: GraphicPlacement) => {
            return item.placementID === activeComponent;
          }
        ) as GraphicPlacement;
        map[component] =
          (placement && {
            id: graphic.id as string,
            viewBox: { ...placement.position },
          }) ||
          null;
      }
      return map;
    },
    // The current graphic setting as viewBox string
    viewBoxByComponent(): Record<string, string> {
      const map: Record<string, string> = {};
      for (let component of this.componentsWithGraphicsSupport) {
        let result = "";
        const settings = this.graphicSettings[component];
        if (settings)
          result = `${settings.viewBox.x} ${settings.viewBox.y} ${settings.viewBox.width} ${settings.viewBox.height}`;
        map[component] = result;
      }
      return map;
    },
    graphicByComponent(): Partial<Record<PlacementTypes, number>> {
      const map: Partial<Record<PlacementTypes, number>> = {};
      for (let component of this.componentsWithGraphicsSupport) {
        const componentObj = this.recipe.recipe_v2.components?.[component];
        const isGraphics = !!(
          componentObj && componentObj.group === "graphics"
        );
        map[component] = isGraphics ? componentObj.swatch_id : "";
      }
      return map;
    },
    recipeId(): string {
      return this.recipe?.recipeId;
    },
    isStandardMenuOpen(): boolean {
      return this.$store.getters["standardNavigation/isStandardMenuOpen"];
    },
    isMenuPreviousSateOpen(): boolean {
      return this.$store.getters["standardNavigation/isPreviousMenuStateOpen"];
    },
    isMobile(): boolean {
      return this.$store.getters["utils/returnScreenSize"].includes("mobile");
    },
    translations(): any {
      return this.$store.state.translations.data;
    },
    isSplashPageModalOpen(): boolean {
      return this.$store.state.utils.isSplashModalOpen;
    },
    hasUgc(): boolean {
      return (this as any).$recipe.checkApparelUGC();
    }
  },
  watch: {
    async showEditor(newValue: boolean) {
      this.cleanSvgInteractionHandler();
      if (!this.activeGraphicID) return;

      await this.updateSvg();

      if (newValue) this.$nextTick(() => this.setSvgInteractionHandler());
    },
    editorVisible(newValue: boolean, oldValue: boolean): void {
      const initialOpacityAttributes: NumericCssAnimatedAttribute = {
        opacity: oldValue ? 1 : 0,
      };
      const initialScaleAttributes: NumericCssAnimatedAttribute = {
        scale: oldValue ? 1 : 0.1,
        other: 3,
      };
      const finalOpacityAttributes: NumericCssAnimatedAttribute = {
        opacity: newValue ? 1 : 0,
      };
      const finalScaleAttributes: NumericCssAnimatedAttribute = {
        scale: newValue ? 1 : 0.1,
        other: 5,
      };
      const mutatingOpacityValues: NumericCssAnimatedAttribute = {
        ...initialOpacityAttributes,
      };
      const mutatingScaleValues: NumericCssAnimatedAttribute = {
        ...initialScaleAttributes,
      };
      const animatedEl: HTMLElement = this.$refs.editorContainer as HTMLElement;
      const opacityAnimation = new TWEEN.default.Tween(mutatingOpacityValues)
        .to(finalOpacityAttributes, 350)
        .easing(TWEEN.default.Easing.Linear.None)
        .onUpdate(() => {
          if (animatedEl)
            animatedEl.style.opacity = mutatingOpacityValues.opacity.toString();
        })
        .onStart(() => {
          // makes the container element visible first
          if (newValue) this.showEditor = true;
        })
        .onComplete(() => {
          // hides the container element after the animation
          if (!newValue) this.showEditor = false;
        });
      const scaleAnimation = new TWEEN.default.Tween(mutatingScaleValues)
        .to(finalScaleAttributes, 250)
        .easing(TWEEN.default.Easing.Cubic.Out)
        .onUpdate(() => {
          if (animatedEl)
            animatedEl.style.transform = `scale(${mutatingScaleValues.scale})`;
        });
      opacityAnimation.start();
      scaleAnimation.start();
    },
    activeComponentAndGraphic: {
      immediate: true,
      handler: async function (): Promise<void> {
        if (!this.recipe?.recipe_v2?.components?.[this.activeComponent]) return;
        if (this.componentsWithGraphicsSupport.includes(this.activeComponent)) {
          this.lastGraphicComponent = this.activeComponent;
        }
        this.cleanSvgInteractionHandler();
        if (this.activeGraphicID) {
          await this.updateSvg();
          this.$nextTick(() => {
            this.setSvgInteractionHandler();
          });
        }
      },
    },
    activePlacementSetting: {
      deep: true,
      handler: async function (): Promise<void> {
        if (!this.recipe?.recipe_v2?.components?.[this.activeComponent]) return;

        if (!this.svgModels?.[this.activeComponent]) return;

        if (
          !this.activeSubComponent &&
          this.builtGraphicID !== this.activeGraphicID
        )
          return;
        if (
          this.activePlacementSetting?.placementComponent !==
          this.activeComponent
        )
          return;

        const component = await FormatRecipeForSVGService({
          componentID: this.activeComponent,
          recipe: this.recipe.recipe_v2,
        });

        if (!component?.sub_components) return;

        const subcomponent =
          component.sub_components?.[this.activeSubComponent] ?? {};

        this.customsSvgService.updateComponent(
          this.svgModels[this.activeComponent],
          this.activeSubComponent,
          subcomponent
        );
        this.cleanSvgInteractionHandler();
        const viewBox = this.graphicSettings[this.activeComponent]!.viewBox;
        this.customsSvgService.updateViewport(
          this.svgModels[this.activeComponent],
          viewBox.x,
          viewBox.y,
          viewBox.width,
          viewBox.height
        );

        this.svgContentStr = this.svgModels[this.activeComponent].getSvg();

        // wait for dom to update
        this.$nextTick(() => {
          this.setSvgInteractionHandler();
        });
      },
    },
    svgContentStr: function (val: string) {
      if (!val) return;

      CanvasRenderer.createImage(
        this.activeComponent,
        CreateWrapperSVG({
          svgSize: this.svgSize,
          innerSVG: val,
          componentID: this.activeComponent,
        }),
        this.svgSize
      );

      // checks if the component is the right sleeve and repeats the operation for the left sleeve
      if (this.activeComponent === "graphic_sleeve_right") {
        CanvasRenderer.createImage(
          "graphic_sleeve_left",
          CreateWrapperSVG({
            svgSize: this.svgSize,
            innerSVG: val,
            componentID: "graphic_sleeve_left",
          }),
          this.svgSize
        );
      }
    },
    showPreloader() {
      this.processSubComponentsFromRecipe();
    },
  },
  methods: {
    removeTexture() {
      // remove SVG string
      this.svgContentStr = "";
      // remove CanvasTexture instance
      CanvasRenderer.removeCanvas(this.activeComponent);
      // checks if the component is the right sleeve and removes for the left sleeve
      if (this.activeComponent === "graphic_sleeve_right")
        CanvasRenderer.removeCanvas("graphic_sleeve_left");
    },
    onZoomIn(){
      this.onZoomToggle();
      this.$nextTick(()=>{
        const isSplashPageShown = this.$cookies.get(sessionKeys.graphicEditorSplashPageShown);
        if(!isSplashPageShown) {
          this.$cookies.set(sessionKeys.graphicEditorSplashPageShown, true, 0);
          this.$store.commit('utils/SET_IS_SPLASH_MODAL_OPEN', true);
          this.$modal.show('splashPageModal');
        }
      });
    },
    onZoomToggle() {
      const isApparel = this.$store.getters["utils/isApparelEnabled"];
      this.$store.commit(
        "graphicsController/SET_EDITOR_VISIBLE",
        !this.editorVisible
      );
      this.setDataLayer(this.editorVisible);
      if (isApparel && this.isMobile) {
        this.hideMenuOnZoom();
      }
    },
    hideMenuOnZoom() {
      const menu = document.getElementById("standard-navigation");
      const arrowIcon = document.querySelector(
        ".standard-navigation__mobile-arrow"
      );
      if (menu && arrowIcon) {
        if (this.editorVisible) {
          menu.classList.remove("standard-navigation-custom--visible");
          arrowIcon.classList.remove("standard-navigation__mobile-arrow--open");
          menu.classList.add("standard-navigation-custom--hidden");
          arrowIcon.classList.add("standard-navigation__mobile-arrow--close");
          this.$store.commit("standardNavigation/IS_STANDARD_MENU_OPEN", false);
        } else {
          if (this.isMenuPreviousSateOpen) {
            menu.classList.remove("standard-navigation-custom--hidden");
            arrowIcon.classList.remove(
              "standard-navigation__mobile-arrow--close"
            );
            menu.classList.add("standard-navigation-custom--visible");
            arrowIcon.classList.remove(
              "standard-navigation__mobile-arrow--open"
            );
            this.$store.commit("standardNavigation/IS_STANDARD_MENU_OPEN", true);
          } else {
            menu.classList.remove("standard-navigation-custom--visible");
            arrowIcon.classList.remove(
              "standard-navigation__mobile-arrow--open"
            );
            menu.classList.add("standard-navigation-custom--hidden");
            arrowIcon.classList.add("standard-navigation__mobile-arrow--close");
            this.$store.commit("standardNavigation/IS_STANDARD_MENU_OPEN", false);
          }
        }
      }
    },
    // Updating the graphic ugc item viewbox's data in the store.
    async onSetActiveGraphicViewbox(point: IViewBox) {
      const { activeComponent } = this;
      const {id: graphicId , default_component: subComponetUGC  } =
        this.$store.getters["gdm/getGraphicByComponent"](activeComponent);
      let ugcDataStore = getUGCDataByID(activeComponent, "fill_ugc");
      const hasModalShown =
        this.graphicsController.outOfBoundsModalShownInfo?.[activeComponent]?.[
          graphicId
        ];

      this.$store.commit("recipe/SET_SUBCOMPONENT_TOOLKIT", {
        componentID: activeComponent,
        subComponentID: subComponetUGC,
        payload: {
          viewBox: point,
          zoomValue: this.zoomValue
        }
      });
      this.updateZoomValue(point);
      (ugcDataStore as UGCDataStore).zoomValue = this.zoomValue;
      setUGCData(ugcDataStore as UGCDataStore);
      // Determine if we should show the out of bounds modal or not
      if (
        !hasModalShown &&
        ugcDataStore?.ugcData &&
        checkImageOutOfBounds(point, {
          width: ugcDataStore.ugcData.width,
          height: ugcDataStore.ugcData.height,
        })
      ) {
        this.$modal.show(!!this.$store.state.utils.configuration.modals?.ugcPlacementError ? "ugcPlacementWarningModal" : "NewPlacementWarningModal");
        this.$store.commit(
          "graphicsController/ADD_OUT_OF_BOUNDS_MODAL_SHOWN_INFO",
          {
            activeComponent,
            graphicId,
          }
        );
      }

      this.$store.dispatch("graphicsController/ADD_GRAPHIC_UGC_ITEM", {
        placement: activeComponent,
        graphicID: graphicId,
        viewBox: point,
      });

      // checks if the component is the right sleeve and repeats the operation for the left sleeve
      if (this.activeComponent === "graphic_sleeve_right") {
        this.$store.dispatch("graphicsController/ADD_GRAPHIC_UGC_ITEM", {
          placement: "graphic_sleeve_left",
          graphicID: graphicId,
          viewBox: point,
        });
      }

      await this.updateSvg();
      this.$nextTick(() => this.setSvgInteractionHandler());
    },
    getConfiguration(
      graphicComponent: GraphicComponent[],
      configurationId: GraphicComponentTypes
    ): any {
      return graphicComponent.find((item) => item.id === configurationId) || {};
    },
    async updateSvg(): Promise<void> {
      if (this.firstLoad) return;
      const recipeComponent = await FormatRecipeForSVGService({
        componentID: this.activeComponent,
        recipe: this.recipe.recipe_v2,
      });
      this.isLoading = true;
      this.svgModels[this.activeComponent] =
        await this.customsSvgService.buildSvg(recipeComponent);
      const viewBox = this.graphicSettings[this.activeComponent]!.viewBox;

      this.customsSvgService.updateViewport(
        this.svgModels[this.activeComponent],
        viewBox.x,
        viewBox.y,
        viewBox.width,
        viewBox.height
      );
      this.svgContentStr = this.svgModels[this.activeComponent].getSvg();
      this.isLoading = false;
      this.builtGraphicID = this.activeGraphicID;
    },
    processSubComponentsFromRecipe(): void {
      const components: IComponentRecipe = cloneDeep(
        this.recipe.recipe_v2?.components
      );

      if (!components) return;
      const getGdmPromises: Promise<IGraphicGDM>[] =
        this.componentsWithGraphicsSupport.map((componentName) => {
          return (components[componentName] &&
            this.$store.dispatch("gdm/GET_GDM", {
              id: components?.[componentName]?.swatch_id, // get the GDM via it's id (graphic swatch ID)
              component3D: componentName,
            })) as Promise<IGraphicGDM>;
        });

      Promise.all(getGdmPromises).then(async () => {
        for (let i = 0; i < this.componentsWithGraphicsSupport.length; i++) {
          const componentName: PlacementTypes = this
            .componentsWithGraphicsSupport[i] as PlacementTypes;

          const graphic = this.gdm.data.find(
            (graphic: IGraphic) =>
              components[componentName] &&
              graphic.id === components?.[componentName]?.swatch_id
          );

          const subComponents = components[componentName]?.sub_components;

          const subComponentKeys: GraphicComponentTypes[] = Object.keys(
            subComponents || {}
          ) as GraphicComponentTypes[];

          const subComponentsFromGdm =
            graphic?.component.map(
              (subComponent: GraphicComponent) => subComponent.id
            ) || [];
          for (let item of subComponentsFromGdm) {
            if (!subComponentKeys.includes(item)) subComponentKeys.push(item);
          }

          for (let j = 0; j < subComponentKeys.length; j++) {
            const subComponentName = subComponentKeys[j];
            const subComponentObj =
              components[componentName]?.sub_components?.[subComponentName];

            if (subComponentObj?.group === "patterns") {
              const swatchObj = this.availableSwatches.find(
                (swatch) => swatch.id === String(subComponentObj.swatch_id)
              );
              const image_url =
                swatchObj?.image_assets?.svg_map ??
                swatchObj?.material_options?.textures?.map.image_url;

              await fetchAndInsertImageToGraphicSettings({
                src: `${this.$store.state.endPoints.customizer.s3SwatchesBucket}assets${image_url}`,
                componentID: componentName,
                subcomponentID: subComponentName as GraphicComponentTypes,
                swatch_id: subComponentObj.swatch_id,
              });

              const recipeComponents = await FormatRecipeForSVGService({
                componentID: componentName,
                subComponentID: subComponentName,
                restrict: true,
                recipe: this.recipe.recipe_v2,
              });

              components[componentName]!.sub_components![subComponentName] =
                recipeComponents?.sub_components![subComponentName];
            }

            if (subComponentObj?.group === "ugc") {
              const url =
                subComponentObj.ugc?.original_image.image_url ??
                (subComponentObj.ugc?.original_image as any).url;
              const image_id =
                subComponentObj.ugc?.original_image.image_id ??
                (subComponentObj.ugc?.original_image as any).temp_id;

              await fetchAndInsertImageToGraphicSettings({
                // the recipe contains url instead of image_url as in the type
                src: url,
                componentID: componentName,
                subcomponentID: subComponentName as GraphicComponentTypes,
                type: "ugc",
                imgId: image_id,
                imgUrl: url,
              });

              await this.$store.dispatch(
                "graphicsController/ADD_GRAPHIC_UGC_ITEM",
                {
                  placement: componentName,
                  graphicID: Number(components?.[componentName]?.swatch_id),
                  viewBox: { ...subComponentObj.toolkit_options?.viewBox },
                }
              );

              const recipeComponents = await FormatRecipeForSVGService({
                componentID: componentName,
                subComponentID: subComponentName,
                restrict: true,
                recipe: this.recipe.recipe_v2,
              });

              components[componentName]!.sub_components![subComponentName] =
                recipeComponents?.sub_components![subComponentName];
            }
            const recipeColor =
              subComponents?.[subComponentName]?.toolkit_options?.hex;

            const graphicComponent = graphic?.component?.find(
              (graphicComponent: GraphicComponent) =>
                graphicComponent.id === subComponentName
            );
            const swatch = this.$store.getters["swatches/returnSwatchObj"]({
              swatchId: graphicComponent!.swatch_id,
            });
            const defaultColor = swatch?.color;
            const placementSettingType =
              recipeColor && recipeColor !== defaultColor
                ? "user_defined"
                : "default";

            this.$store.commit("graphicsController/SET_PLACEMENT_SETTING", {
              placementComponent: componentName,
              componentSetting: {
                id: subComponentName,
                swatch_id: Number(graphicComponent!.swatch_id),
                hex: recipeColor || defaultColor || "#000000",
              },
              type: placementSettingType,
            });
          }

          if (components[componentName]) {
            this.precreatedRecipe = true;
            this.svgModels[componentName] =
              await this.customsSvgService.buildSvg(components[componentName]);
            this.renderInitialGraphic(componentName);
          }
          this.firstLoad = false;
        }
        if (this.queryParams.edit && !this.firstLoad) {
          this.openQuickEditOnInitialLoad();
          this.$customizerDataLayer.logAnalyticsEvent({
            type: this.$customizerDataLayer.types.customizerUgcFlow,
            eventLabel: 'Finished - External Flow',
            forceEcomDataLayerFormat: true,
          });
        }
        this.$nextTick(()=>{
          this.$eventBus.$emit("splash-page-graphic-editor-event");
        });
      });
    },
    openQuickEditOnInitialLoad(
      component: PlacementTypes = "graphic_front"
    ): void {
      this.$store.commit("ugc/tempData/SET_ACTIVE_UGC_COMPONENT", component);
      this.$store.commit("legacyNavigation/SET_ACTIVE_COMPONENT", component);
      this.$store.commit(
        "standardNavigation/SET_ACTIVE_COMPONENT_STANDARD_NAVIGATION",
        component
      );
      this.$store.commit("graphicsController/SET_EDITOR_VISIBLE", true);
    },
    renderInitialGraphic(componentID: PlacementTypes) {
      const viewBox = this.graphicSettings[componentID]?.viewBox;
      this.precreatedRecipe = false;
      if (viewBox)
        this.customsSvgService.updateViewport(
          this.svgModels[componentID],
          viewBox.x,
          viewBox.y,
          viewBox.width,
          viewBox.height
        );

      const svgSize = (this.$store.state.utils.pdm as IPDM).components[
        componentID
      ].svg_size!;

      if (this.svgModels[componentID])
        CanvasRenderer.createImage(
          componentID,
          CreateWrapperSVG({
            svgSize: svgSize,
            innerSVG: this.svgModels[componentID].getSvg(),
            componentID: componentID,
          }),
          svgSize
        );
    },
    // removes the previous svgInteractionHandler
    cleanSvgInteractionHandler(): void {
      this.svgInteractionHandler?.destroy();
      this.svgInteractionHandler = null;
    },
    setSvgInteractionHandler(): void {
      const editor = this.$refs.svgEditor as GraphicSvgEditor;
      let svgElement: SVGElement | undefined =
        editor && ((editor as Vue).$el.firstElementChild as SVGElement);
      if (svgElement) {
        const originalGraphic = this.$store.getters["graphicsController/getOriginalGraphicViewBox"]();
        if(!originalGraphic && this.hasUgc) {
          const graphic = this.$store.getters["gdm/getGraphicByComponent"](this.activeComponent);
          this.zoomValue = this.recipe.initRecipe?.components[this.activeComponent]?.sub_components?.[graphic.default_component]?.toolkit_options?.zoomValue || "100";
        } else {
          this.zoomValue = originalGraphic?.zoomValue;
        }
        this.svgInteractionHandler = new SVGInteractionHandler(
          svgElement as SVGElement,
          this.onSetActiveGraphicViewbox
        );
      }
    },
    setDataLayer(showEditor: boolean): void {
      const eventType = showEditor
        ? this.$customizerDataLayer.types.customizerQuickAdjust
        : this.$customizerDataLayer.types.customizerDoneAdjust;
      const eventLabel = showEditor ? "Quick Adjust" : "Done";
      this.$customizerDataLayer.logAnalyticsEvent({
        type: eventType,
        eventLabel: eventLabel,
        forceEcomDataLayerFormat: true,
      });
    },
    onChange(ev: any): void {
      this.setZoom(parseInt(ev.target?.value));
    },
    zoomOut(): void {
      const value = parseInt(this.zoomValue);
      let zoomValue = value >= this.maxValue ? this.maxValue : value + 1;
      this.setZoom(zoomValue);
    },
    zoomIn(): void {
      const value = parseInt(this.zoomValue);
      let zoomValue = value <= this.minValue ? this.minValue : value - 1;+
      this.setZoom(zoomValue);
    },
    getZoomPorcentage(zoomInterval: number): number {
      return 2 - (zoomInterval / 100);
    },
    setZoom(zoomInterval: number): void {
      this.svgInteractionHandler?.setZoom(this.getZoomPorcentage(zoomInterval));
      this.zoomValue = zoomInterval.toString();
      this.setZoomValue();
    },
    updateZoomValue(point: IViewBox): void {
      const originalGraphic = this.$store.getters["graphicsController/getOriginalGraphicViewBox"]();
      const porcentage = this.maxValue - ((point.height * this.maxValue)/(originalGraphic?.IPoint?.height * 2));
      this.zoomValue = porcentage.toString();
      this.setZoomValue();
    },
    setZoomValue(){
      this.$store.commit("graphicsController/SET_ORIGINAL_GRAPHIC_ZOOM_VIEWBOX", {
        component: this.activeComponent,
        graphic: this.$store.getters["gdm/getGraphicByComponent"](this.activeComponent),
        graphicViewZoomBox: { zoomValue: this.zoomValue }
      });
    }
  },
  created() {
    this.$eventBus.$on(
      "graphic-scene-updated",
      this.processSubComponentsFromRecipe
    );
  },
  beforeDestroy() {
    CanvasRenderer.destroyAll();
    this.$eventBus.$off(
      "graphic-scene-updated",
      this.processSubComponentsFromRecipe
    );
  },
  mounted() {
    this.showEditor = this.graphicsController.editorVisible;
    this.customsSvgService = {};
    // this.customsSvgService = new SvgService({
    //   assetsBucket:
    //     this.$store.state.endPoints.customizer.customsSvgServiceEndpoint ??
    //     "vans-customs-assets-dev",
    // });
    this.$eventBus.$on("remove-texture", this.removeTexture);
    CanvasRenderer.model3d = this.$model3D;
  },
});
